/**
 * Phase 18 helpers shared by the entry forms, the list and the renderers.
 */
import type { Entry } from '../types';
import type { EntryVisibility, NoteCategory } from '../../../../dtos/patientmr/charting';
import { disciplineForProviderType, getDisciplineEntry, therapyDisciplines } from '../../../../constants/disciplines';

/** Display label for an upper-case code: NOT_TOLERATED -> Not tolerated */
export const codeLabel = (code: string): string =>
  code ? code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ') : '';

/** M13: note category labels (shown in the select and the list) */
export const NOTE_CATEGORY_LABELS: Record<NoteCategory, string> = {
  GENERAL: 'General',
  TEACHING: 'Teaching',
  MD_NOTIFICATION: 'MD notification',
  NO_CARE: 'No care',
  HANDOFF: 'Handoff',
  SUPERVISORY: 'Supervisory',
};

export const VISIBILITY_HELP = 'Not shown to the family or on external copies. Still part of the record.';

/** Entry types that carry visibility (M6) */
export const VISIBILITY_ENTRY_TYPES = ['notes', 'incident', 'addendum', 'supervisory'] as const;

/** M6: INTERNAL when data.visibility says so or (INCIDENT) internalOnly is true */
export const entryVisibility = (entry: Pick<Entry, 'data'>): EntryVisibility => {
  const data = entry.data && typeof entry.data === 'object' ? (entry.data as { visibility?: unknown; internalOnly?: unknown }) : null;
  if (!data) return 'CHART';
  return data.visibility === 'INTERNAL' || data.internalOnly === true ? 'INTERNAL' : 'CHART';
};

/** M13: SUPERVISORY visit, case-insensitive ("Supervisory", "SUPERVISORY") */
export const isSupervisoryVisit = (visitType: string | null | undefined): boolean =>
  String(visitType || '').trim().toUpperCase() === 'SUPERVISORY';

/** Phase 20A: a therapy discipline or a therapy provider type (PTA, COTA, SLPA, SLP) */
export const isTherapyDiscipline = (discipline: string | null | undefined): boolean =>
  therapyDisciplines().includes(disciplineForProviderType(discipline) || '');

/** M8 quick-add order */
export const NURSING_QUICK_ADD = ['vitals', 'mar', 'respiratory', 'seizure', 'feed', 'output', 'notes', 'wound', 'incident', 'summary', 'measurements'] as const;
// Phase 20C: the treatment note (procedures billed) comes first on a therapy visit
export const THERAPY_QUICK_ADD = ['therapy', 'notes', 'vitals', 'summary', 'measurements', 'incident'] as const;

// Phase 20E: after the discipline's own note
export const DISCIPLINE_NOTE_QUICK_ADD = ['notes', 'vitals', 'summary', 'incident'] as const;

// Phase 20D: aide and attendant visits start with the task note
export const AIDE_QUICK_ADD = ['aidetasks', 'notes', 'vitals', 'summary', 'incident'] as const;

/** Phase 20D: an aide or attendant discipline or provider type */
export const isAideTaskDiscipline = (discipline: string | null | undefined): boolean =>
  Boolean(getDisciplineEntry(disciplineForProviderType(discipline))?.aideTasks);

/** Quick-add types for a visit; the supervisory note comes first on a supervisory visit (F21).
 * Phase 20F: a nursing visit of a patient in the palliative care programme offers the symptom entry. */
export const quickAddTypes = (discipline: string | null | undefined, visitType: string | null | undefined, options: { palliative?: boolean } = {}): string[] => {
  // Phase 20E: MSW, RT, RD and BH visits start with their discipline's note
  const noteType = getDisciplineEntry(disciplineForProviderType(discipline))?.noteType;
  const base: string[] = isTherapyDiscipline(discipline) ? [...THERAPY_QUICK_ADD]
    : isAideTaskDiscipline(discipline) ? [...AIDE_QUICK_ADD]
      : noteType ? [noteType.toLowerCase(), ...DISCIPLINE_NOTE_QUICK_ADD] : [...NURSING_QUICK_ADD];
  if (options.palliative && !isTherapyDiscipline(discipline) && !isAideTaskDiscipline(discipline) && !noteType) base.splice(1, 0, 'symptom');
  return isSupervisoryVisit(visitType) ? ['supervisory', ...base] : base;
};

/** When the care happened: observedAt, else the entry's timestamp */
export const entryTimeMs = (entry: Pick<Entry, 'observedAt' | 'date'>): number => {
  const t = Date.parse(entry.observedAt || entry.date || '');
  return Number.isFinite(t) ? t : 0;
};

/** Newest first by observed time */
export const sortByObservedDesc = <T extends Pick<Entry, 'observedAt' | 'date'>>(entries: T[]): T[] =>
  [...entries].sort((a, b) => entryTimeMs(b) - entryTimeMs(a));

/** Most recent, not invalidated entry of a local type */
export const latestEntryOfType = (entries: Entry[], type: string): Entry | undefined =>
  sortByObservedDesc(entries.filter((e) => e && e.type === type && !e.invalidated))[0];

/** "2:05 PM" for an ISO time ('' when it cannot be parsed) */
export const clockLabel = (iso: string | null | undefined): string => {
  const t = Date.parse(iso || '');
  return Number.isFinite(t) ? new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
};

/** "09:40" (24 h) for an ISO time, as in "last given 09:40" */
export const clock24 = (iso: string | null | undefined): string => {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
